<template>
  <Loader :value="promises.fetchOne" class="residential-template-mutate view" :message="$metaInfo.title" :key="id">
    <div class="container">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #right>
          <div class="row wrap g-1">
            <ButtonComponent
              v-if="!isNew"
              tag="router-link"
              :to="{ name: 'residential-template-leads', params: { reference: id } }"
              :label="$tc('lead', 2)"
              :icon="['fal', 'users']"
            />
            <Actions placement="residentialTemplate" :query="{ residentialTemplate: id }" />

            <ButtonComponent
              :label="saveState.label"
              :success-label="$t('saved')"
              :loading-label="$t('saving')"
              :icon="['fal', saveState.icon]"
              theme="primary"
              :promise="promises.mutate"
              :disabled="!validator.passes"
              :class="{
                'elder-button--disabled': saveState.disabled,
                'elder-button--invalid': saveState.invalid,
              }"
              v-tooltip="{ content: saveState.tooltip }"
              @click="submit"
            />
          </div>
        </template>
      </HeaderComponent>
      <StepperComponent :value="steps" ref="stepper" state-handler="route" @submit="submit">
        <template #after="{ meta, next, prev }">
          <div class="row center g-1">
            <ButtonComponent
              v-if="meta.prev"
              :label="$t('prev')"
              :type="isNew ? 'button' : 'submit'"
              :icon="['fal', 'angle-left']"
              icon-placement="left"
              @click="onNavigation(prev)"
            />
            <ButtonComponent
              v-if="meta.next"
              :label="$t('next')"
              :type="isNew ? 'button' : 'submit'"
              :icon="['fal', 'angle-right']"
              @click="onNavigation(next)"
            />
            <ButtonComponent
              v-if="!meta.next"
              :label="$t('complete')"
              :success-label="$t('completed')"
              :loading-label="$t('saving')"
              :icon="['fal', 'check']"
              theme="primary"
              type="button"
              :disabled="!validator.passes"
              :promise="promises.mutate"
              @click="complete"
            />
          </div>
        </template>
      </StepperComponent>
    </div>
  </Loader>
</template>

<script>
import Model from '@/models/ResidentialTemplate/base'
import ModelMixin from '@/mixins/Model'
import ValidatorMixin from '@/mixins/validator-v2'
import { getValidatorConfig } from '@kvass/custom-fields'
import { mapState, mapActions, mapMutations } from 'vuex'
import { mergeDeepRight } from 'ramda'
import fields from '@/components/Residential/StepsFields/fields.js'
import defaultFields from '@/components/Residential/StepsFields/defaults.js'
import { getFieldsValues, setFieldsValue } from '@/components/Residential/StepsFields/utils.js'
import { buildStepsFields, setByPath, mergeAllDeepRight } from '@/utils'

import Steps from './Steps'
import { StepperComponent } from 'vue-elder-stepper'
import { DropdownComponent } from 'vue-elder-dropdown'
import HeaderComponent from '@/components/Header'
import Actions from '@/components/Actions'
import { getExtensions } from '@/components/Tiptap/Extensions'

export default {
  mixins: [
    ValidatorMixin({
      rules: 'rules',
      labels: 'labels',
      data: 'internalData',
    }),
    ModelMixin({
      source: 'item',
      guard: true,
      model: Model,
      transform: {
        // the renderer writes back into shared `options`/`schema` references,
        // so keep schemas out of the diff entirely (see other Mutate views)
        'customFieldsConfig.*.schema': undefined,
        properties: arr =>
          arr.map((a, index) => {
            if (a.value) a.value = a.value.toString()
            a.order = index + 1
            return a
          }),
      },
    }),
  ],
  props: {
    id: String,
  },
  provide() {
    return {
      RichTextOptions: {
        commands: getExtensions(),
        button: {
          internalItems: `/api/resource/post?field.label=title&field.value=id&field.thumbnail=media.cover&references=${this.id}`,
          anchorItems: [{ value: '#lead', label: this.$t('leadForm') }],
          colors: ['primary', 'secondary', 'custom'],
          enableIcon: true,
        },
      },
    }
  },
  data() {
    return {
      internalNameState: false,
      internalData: {},
      view: 'residential-template',
    }
  },
  watch: {
    id: {
      handler(id) {
        if (this.isNew) {
          this.set()
          this.internalData = this.valueComp
          return
        }

        return this.fetch({ id }).then(() => {
          this.internalData = this.valueComp
          if (!this.item.id) return this.$router.replace({ name: 'notfound' })
        })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('ResidentialTemplate', ['item', 'promises']),

    labels() {
      return {
        ...this.fieldValidatorConfig.labels,
      }
    },
    rules() {
      return {
        ...this.fieldValidatorConfig.rules,
      }
    },

    saveState() {
      let base = {
        label: this.$t('save'),
        icon: 'arrow-to-bottom',
        disabled: !this.validator.passes || !this.hasChanges,
      }

      if (!this.validator.passes)
        return {
          ...base,
          invalid: true,
          icon: 'exclamation-circle',
          tooltip: Object.values(this.validator.errors.all()).join('<br>'),
        }

      if (!this.hasChanges)
        return {
          ...base,
          tooltip: this.$t('noUnsavedChanges'),
        }

      return base
    },
    isNew() {
      return !this.id
    },
    breadcrumb() {
      return [
        { title: this.$tc('unitCatalog', 1), action: { name: 'residential-templates' } },
        { title: this.$metaInfo.title },
      ]
    },
    fieldValidatorConfig() {
      if (!this.customFieldSchemas.length) return {}
      return getValidatorConfig([...this.customFieldSchemas], this.data, { prefix: '' })
    },

    customFieldSchemas() {
      return [
        ...[...Array.from(defaultFields.values() || []).map(i => i(this))].filter(i => {
          const placement = i.placement instanceof Array ? i.placement : [i.placement]
          return placement.map(p => p.split(':').shift()).includes('residential-template')
        }),
        ...[...(this.$path('data.customFieldsConfig') || [])]
          .map(c =>
            c.schema.map(e => ({
              ...e,
              placement: c.placement || e.placement || 'residential-template:presentation',
              dataKey: 'customFields',
              isCustom: true,
            })),
          )
          .flat(),
      ]
    },
    valueComp: {
      get() {
        //map data to fields
        return {
          ...getFieldsValues(this.customFieldSchemas, {
            ...this.data,
            showInternalName: this.showInternalName,
          }),
          ...this.data,
          ...(this.$path('customFields', this.data) || {}),
          view: this.view,
        }
      },
      set(newVal) {
        //if slot -> update single field
        if (newVal.slot) {
          const { key, value } = newVal
          return setByPath(key, value, this.data)
        }

        const fieldsData = setFieldsValue(newVal, this.customFieldSchemas)
        //not slot -> customFields object is newVal -> Tranform this to fit merge with data
        this.data = mergeAllDeepRight(this.data, fieldsData)
        //only sync on a real change — a redundant false->false assignment would
        //trigger the setter and null out internalName (the template's display name)
        if ('showInternalName' in fieldsData && fieldsData.showInternalName !== this.showInternalName) {
          this.showInternalName = fieldsData.showInternalName
        }

        this.internalData = newVal
      },
    },
    steps() {
      const data = mergeDeepRight(this.item, { ...this.data, userRoles: this.$store.getters })

      let props = {
        data,
        value: this.valueComp,
        labels: this.labels,
        validator: this.validator,
        promises: this.promises,
        id: this.data.id,
        customFieldSchemas: this.customFieldSchemas,
        fields: buildStepsFields(Array.from(fields.values()), this.customFieldSchemas, this),
      }

      let listeners = {
        submit: () => this.submit(),
        updateValue: e => (this.valueComp = e),
      }

      return Steps(data).map(raw => {
        const { icon, ...s } = raw
        s.props = {
          ...(s.props || {}),
          ...props,
        }
        s.listeners = {
          ...(s.listeners || {}),
          ...listeners,
        }
        s.disabled = ['shop', 'roles'].includes(raw.id) && this.isNew
        return s
      })
    },
    showInternalName: {
      get() {
        if (!this.data) return
        return this.internalNameState || (this.data.internalName && Boolean(this.data.internalName !== this.data.name))
      },
      set(val) {
        if (val) this.internalNameState = true
        else {
          this.internalNameState = false
          this.data.internalName = null
        }
      },
    },
  },
  methods: {
    ...mapActions('ResidentialTemplate', ['fetch', 'mutate', 'remove']),
    ...mapMutations('ResidentialTemplate', ['set']),
    submit() {
      if (!this.validator.passes || !this.hasChanges) return Promise.resolve()

      // customFieldsConfig is read-only — ResidentialTemplateUpdate does not accept it
      const { customFieldsConfig, ...payload } = this.getData({ diff: true })

      return this.mutate({
        ...payload,
        id: this.id,
      }).then(data => {
        if (!this.isNew) return
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)

        this.$router.replace({
          name: 'residential-template-edit',
          params: { id: data.id, step: this.$route.params.step || 'presentation' },
        })
      })
    },
    complete() {
      this.submit().then(() => this.$router.push({ name: 'residential-templates' }))
    },

    onNavigation(callback) {
      callback()
      this.submit()
    },
  },
  metaInfo() {
    return {
      title: this.isNew ? this.$t('createUnitCatalog') : this.$path('item.internalName') || this.$path('item.name'),
    }
  },
  components: {
    HeaderComponent,
    StepperComponent,
    DropdownComponent,
    Actions,
  },
}
</script>

<style lang="scss">
.residential-template-mutate {
  background-color: $background-grey;

  .elder-stepper__component > * {
    margin: 2rem auto;
  }
}
</style>
